import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, ArrowLeft, Phone, Mail } from 'lucide-react';
import { fetchNewsPostBySlug } from '../lib/api';
import Lightbox from '../components/Lightbox';

export default function NewsDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadPost() {
      setLoading(true);
      const data = await fetchNewsPostBySlug(slug);
      setPost(data);
      setLoading(false);
    }
    loadPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex flex-col items-center justify-center px-4 text-center space-y-4">
        <h1 className="text-4xl font-bold uppercase tracking-widest">Oplægget blev ikke fundet</h1>
        <p className="text-base text-slate-600 max-w-md font-light">Vi kunne desværre ikke finde det oplæg, du leder efter. Venligst tjek URL-adressen eller gå tilbage til nyhedsoversigten.</p>
        <Link to="/nyheder" className="px-8 py-3 bg-sky-500 hover:bg-sky-600 text-white font-bold uppercase tracking-wider transition-all">
          Tilbage til oversigten
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white text-slate-600 min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[45vh] min-h-[300px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105"
          style={{ 
            backgroundImage: `url(${post.images?.[0] || 'https://assets.auxodev.dk/assets/814e446f-d222-4d23-8be7-5a6a25bb15ff/6ccf7eb1-0ddd-4c73-aab3-801631a74b5c.jpg'})` 
          }}
        />
        <div className="absolute inset-0 bg-black/45" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center w-full space-y-4">
          <div className="flex items-center justify-center gap-1.5 text-xs text-sky-400 font-semibold uppercase tracking-widest">
            <Calendar className="w-4 h-4" />
            <span>{post.date}</span>
          </div>
          <h1 className="text-xl sm:text-3xl lg:text-5xl font-bold text-white tracking-widest uppercase break-words px-4">
            {post.title}
          </h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-16 sm:py-24 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Main Column */}
          <div className="lg:col-span-8 space-y-10">
            {/* Back Button */}
            <Link 
              to="/nyheder" 
              className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-900 transition-colors group"
            >
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Tilbage til oversigten
            </Link>

            {/* Post Content */}
            <div 
              className="w-full text-slate-600 font-light leading-relaxed
                [&_p]:text-slate-600 [&_p]:font-light [&_p]:leading-relaxed [&_p]:mb-6
                [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-slate-900 [&_h1]:mt-10 [&_h1]:mb-4 [&_h1]:uppercase [&_h1]:tracking-normal
                [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-slate-900 [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:uppercase [&_h2]:tracking-normal [&_h2]:border-b [&_h2]:border-slate-200 [&_h2]:pb-2
                [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-slate-900 [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:uppercase [&_h3]:tracking-normal
                [&_h4]:text-lg [&_h4]:font-bold [&_h4]:text-slate-900 [&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:uppercase [&_h4]:tracking-normal
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:mb-6 [&_ul]:text-slate-600 [&_ul]:font-light
                [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-2 [&_ol]:mb-6 [&_ol]:text-slate-600 [&_ol]:font-light
                [&_li]:pl-1
                [&_strong]:font-semibold [&_strong]:text-slate-900
                [&_a]:text-[#0073A5] [&_a]:underline [&_a]:hover:text-sky-700"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Images Grid */}
            {post.images && post.images.length > 0 && (
              <div className="space-y-6 pt-12 border-t border-slate-200">
                <h3 className="text-2xl font-bold text-slate-900 tracking-normal uppercase">Billeder fra projektet</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {post.images.map((img, idx) => (
                    <div 
                      key={idx}
                      onClick={() => setLightboxIndex(idx)}
                      className="aspect-square bg-slate-100 border border-slate-200 overflow-hidden cursor-pointer group relative"
                    >
                      <img 
                        src={img} 
                        alt={`${post.title} - Billede ${idx + 1}`} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-xs text-white uppercase tracking-wider font-semibold bg-sky-950/90 px-4 py-2 border border-sky-900">
                          Forstør
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar Column */}
          <div className="lg:col-span-4 space-y-8">
            {/* Quick Contact Box */}
            <div className="bg-slate-50 border border-slate-200 p-6 space-y-6">
              <h3 className="text-slate-900 font-bold text-lg tracking-normal border-b border-slate-200 pb-3 uppercase">Spørgsmål til projektet?</h3>
              <p className="text-base text-slate-500 font-light leading-relaxed">
                Vil du vide mere om dette projekt, eller har du en lignende opgave? Kontakt os i dag.
              </p>
              <div className="space-y-4 text-xs">
                <a 
                  href="tel:+4576991020" 
                  className="flex items-center gap-3 p-3 bg-white border border-slate-200 hover:border-sky-500 transition-all group"
                >
                  <Phone className="w-4 h-4 text-sky-500 shrink-0" />
                  <span className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors whitespace-nowrap">(+45) 76 99 10 20</span>
                </a>
                <a 
                  href="mailto:info@dan-team.com" 
                  className="flex items-center gap-3 p-3 bg-white border border-slate-200 hover:border-sky-500 transition-all group"
                >
                  <Mail className="w-4 h-4 text-sky-500 shrink-0" />
                  <span className="font-semibold text-slate-900 group-hover:text-sky-600 transition-colors whitespace-nowrap">info@dan-team.com</span>
                </a>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Lightbox for Gallery */}
      {lightboxIndex !== null && (
        <Lightbox
          images={post.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={setLightboxIndex}
        />
      )}
    </div>
  );
}